"use client";

import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useRef,
} from "react";
import "./modalContext.scss";
import { TimesIcon } from "@/icons";
import Draggable from "react-draggable";

interface ModalContextProps {
  openModal: (
    id: string,
    title: string,
    type: "withOverlay" | "withoutOverlay" | "draggable",
    content: ReactNode
  ) => void;
  closeModal: () => void;
}

const ModalContext = createContext<ModalContextProps | undefined>(undefined);

export const ModalContextProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [type, setType] = useState<
    "withOverlay" | "withoutOverlay" | "draggable"
  >("withoutOverlay");
  const [content, setContent] = useState<ReactNode | null>(null);
  const [currentModalId, setCurrentModalId] = useState<string>("");
  const [title, setTitle] = useState("");
  const [modalKey, setModalKey] = useState(0); // wrapper key
  const nodeRef = useRef<HTMLDivElement>(null);

  const openModal = (
    id: string,
    title: string,
    type: "withOverlay" | "withoutOverlay" | "draggable",
    newContent: ReactNode
  ) => {
    setTitle(title);
    setContent(newContent);
    setCurrentModalId(id);
    setType(type);
    setModalKey((prev) => prev + 1); // her açılışta yeniden mount
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setCurrentModalId("");
    setContent(null);
  };

  return (
    <ModalContext.Provider value={{ openModal, closeModal }}>
      {children}

      {isOpen && type === "withOverlay" && <div className="overlay" />}

      {isOpen && type === "withoutOverlay" && (
        <Draggable
          cancel=".close-button, .modal-content"
          nodeRef={nodeRef as React.RefObject<HTMLElement>}
        >
          <div
            key={modalKey}
            ref={nodeRef}
            className={`modal open ${type}`}
            style={{
              top: "30%",
              left: "30%",
              transform: "translate(-50%, -50%)",
              position: "absolute",
            }}
          >
            <div className="modal-header">
              <span className="modal-header-text">{title}</span>
              <button className="close-button" onClick={closeModal}>
                <TimesIcon className={undefined} />
              </button>
            </div>
            <div className="modal-content">{content}</div>
          </div>
        </Draggable>
      )}

      {isOpen && type !== "withoutOverlay" && (
        <div key={modalKey} className={`modal open ${type}`}>
          <div className="modal-header">
            <span className="modal-header-text">{title}</span>
            <button className="close-button" onClick={closeModal}>
              <TimesIcon className={undefined} />
            </button>
          </div>
          <div className="modal-content">{content}</div>
        </div>
      )}
    </ModalContext.Provider>
  );
};

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) throw new Error("useModal must be used within a ModalProvider");
  return context;
};
